import { SecurityContext, ElementRef } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Component, effect, inject, input, output, signal } from '@angular/core';
import { Marked } from 'marked';
import { markedHighlight } from 'marked-highlight';
import hljs from 'highlight.js/lib/core';
import langBash from 'highlight.js/lib/languages/bash';
import langCss from 'highlight.js/lib/languages/css';
import langJavascript from 'highlight.js/lib/languages/javascript';
import langJson from 'highlight.js/lib/languages/json';
import langMarkdown from 'highlight.js/lib/languages/markdown';
import langPlaintext from 'highlight.js/lib/languages/plaintext';
import langPython from 'highlight.js/lib/languages/python';
import langSql from 'highlight.js/lib/languages/sql';
import langTypescript from 'highlight.js/lib/languages/typescript';
import langXml from 'highlight.js/lib/languages/xml';
import langYaml from 'highlight.js/lib/languages/yaml';

// Register only the languages we expect to see in chat responses so the bundle
// stays small; unknown snippets fall back to `plaintext` below.
const hljsLanguages = {
  bash: langBash,
  sh: langBash,
  shell: langBash,
  css: langCss,
  javascript: langJavascript,
  js: langJavascript,
  json: langJson,
  markdown: langMarkdown,
  md: langMarkdown,
  plaintext: langPlaintext,
  text: langPlaintext,
  python: langPython,
  py: langPython,
  sql: langSql,
  typescript: langTypescript,
  ts: langTypescript,
  xml: langXml,
  html: langXml,
  yaml: langYaml,
  yml: langYaml,
};
for (const [name, language] of Object.entries(hljsLanguages)) {
  hljs.registerLanguage(name, language);
}
import { ChatMessage } from '../../core/models/chat-message';
import { AgentChatService } from '../../pages/chat/services/agent-chat.service';
import { ChatPersistenceService } from '../../core/services/chat-persistence.service';
import { RelatedTopicsService } from '../../core/services/related-topics.service';
import { BillSplitterComponent } from '../../features/bill-splitter/bill-splitter.component';
import { BillHistoryComponent } from '../../features/bill-splitter/bill-history.component';

const markdownHighlighter = markedHighlight({
  emptyLangClass: 'hljs',
  langPrefix: 'hljs language-',
  highlight: (code, language) =>
    hljs.getLanguage(language)
      ? hljs.highlight(code, { language }).value
      : hljs.highlightAuto(code).value,
});

const markdown = new Marked(markdownHighlighter);

@Component({
  selector: 'app-message-list',
  imports: [BillSplitterComponent, BillHistoryComponent],
  templateUrl: './message-list.html',
  styleUrl: './message-list.scss',
})
export class MessageListComponent {
  private readonly sanitizer = inject(DomSanitizer);
  readonly messages = input.required<ChatMessage[]>();
  readonly topicThreadId = input.required<string>();
  /** `undefined` means the current turn has not had topics generated yet; an empty array is cached. */
  readonly cachedTopics = input<string[] | undefined>(undefined);
  readonly isRetrieving = input(false);
  readonly retrievalStatus = input('Choosing the best source…');
  readonly topicSelected = output<string>();
  // Show/hide the matched rag chunks for the most recent bot response.
  private readonly agentChat = inject(AgentChatService);
  private readonly chatPersistence = inject(ChatPersistenceService);
  private readonly relatedTopics = inject(RelatedTopicsService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private topicRequestId = 0;
  private lastTopicReplyKey = '';
  /** While true, content growth (and typing in the composer) keeps the list pinned to the bottom. */
  private stickToBottom = true;
  showContext = false;
  protected readonly topics = signal<string[]>([]);
  protected readonly isGeneratingTopics = signal(false);

  constructor() {
    effect(() => {
      this.refreshRelatedTopics(this.messages(), this.cachedTopics(), this.isRetrieving());
    });
    const listEl = (this.host.nativeElement as HTMLElement).querySelector<HTMLElement>('.message-list');
    if (listEl) listEl.addEventListener('scroll', () => this.onListScroll(listEl));
  }

  /** Keep the conversation pinned to the latest message when typing or content grows. */
  scrollToBottom(): void {
    const listEl = (this.host.nativeElement as HTMLElement).querySelector<HTMLElement>('.message-list');
    if (!listEl) return;
    listEl.scrollTop = listEl.scrollHeight;
    this.stickToBottom = true;
  }

  private onListScroll(listEl: HTMLElement): void {
    const distanceFromBottom = listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight;
    this.stickToBottom = distanceFromBottom < 80;
  }

  protected selectTopic(topic: string): void {
    this.topicSelected.emit(topic);
  }

  /** Pin to the bottom whenever the conversation grows and we are already following it. */
  protected readonly followEffect = effect(() => {
    void this.messages();
    if (this.stickToBottom) this.scrollToBottom();
  });

  toggleContext(): void {
    this.showContext = !this.showContext;
  }

  get contextText(): string {
    return this.agentChat.getLastContext().content;
  }

  get contextLabel(): string {
    return this.agentChat.getLastContext().route === 'web' ? 'web sources' : 'document context';
  }

  protected renderMarkdown(text: string): string {
    const html = markdown.parse(text, { async: false });
    return this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
  }

  private refreshRelatedTopics(messages: ChatMessage[], cachedTopics: string[] | undefined, isRetrieving: boolean): void {
    if (!this.chatPersistence.getEffectiveSettings().relatedTopicGenerationEnabled) {
      this.topicRequestId++;
      this.lastTopicReplyKey = '';
      this.topics.set([]);
      this.isGeneratingTopics.set(false);
      return;
    }

    const lastMessage = messages.at(-1);
    if (!lastMessage || lastMessage.author !== 'bot') {
      this.lastTopicReplyKey = '';
      this.topicRequestId++;
      this.topics.set([]);
      this.isGeneratingTopics.set(false);
      return;
    }

    // A Bill Splitter invocation is an interactive workspace rather than a
    // conversational response. Do not spend a secondary model request on
    // follow-up topics for it (or display any topics cached before it opened).
    if (lastMessage.toolInvocation) {
      this.lastTopicReplyKey = '';
      this.topicRequestId++;
      this.topics.set([]);
      this.isGeneratingTopics.set(false);
      return;
    }

    // Streaming changes the assistant message for every token. Wait until it has
    // completed before generating topics, otherwise each update starts a request.
    if (isRetrieving) {
      this.topicRequestId++;
      this.topics.set([]);
      this.isGeneratingTopics.set(false);
      return;
    }

    const replyKey = messages.map((message) => `${message.author}:${message.time}:${message.text}`).join('\n');
    if (cachedTopics !== undefined) {
      this.topicRequestId++;
      this.lastTopicReplyKey = replyKey;
      this.topics.set(cachedTopics);
      this.isGeneratingTopics.set(false);
      return;
    }

    if (replyKey === this.lastTopicReplyKey) return;
    this.lastTopicReplyKey = replyKey;
    void this.updateRelatedTopics(this.topicThreadId(), messages);
  }

  private async updateRelatedTopics(threadId: string, messages: ChatMessage[]): Promise<void> {
    const requestId = ++this.topicRequestId;
    this.topics.set([]);
    this.isGeneratingTopics.set(true);
    try {
      const topics = await this.relatedTopics.generate(messages);
      if (requestId === this.topicRequestId && this.chatPersistence.getEffectiveSettings().relatedTopicGenerationEnabled) {
        this.topics.set(topics);
        this.chatPersistence.setRelatedTopics(threadId, topics);
      }
    } catch {
      // Related topics are optional; a failed secondary LLM request should not affect chat.
      if (requestId === this.topicRequestId) {
        this.topics.set([]);
        this.chatPersistence.setRelatedTopics(threadId, []);
      }
    } finally {
      if (requestId === this.topicRequestId) this.isGeneratingTopics.set(false);
    }
  }
}
